import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  TextField,
  Button,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Snackbar,
  Alert,
  MenuItem,
  Select,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { theme } from "../../theme";
import ICategoryVariant from "../../entities/ICategoryVariant";
import IProductSku from "../../entities/IProductSku";
import { IMedia } from "../../entities/IMedia";
import CategoryService from "../../services/CategoryService";
import ProductSkuService from "../../services/ProductSkuService";
import { unwrapData, unwrapList, getErrorMessage, getMediaSrc } from "../../services/apiUtils";
import { promoDateToMs } from "../../services/textUtils";



const comboInputSx = {
  minWidth: 110,
  "& .MuiInputBase-input": {
    fontSize: "0.95rem",
    padding: "10.5px 12px",
  },
};

const sectionStyle = {
  backgroundColor: "white",
  borderRadius: "12px",
  boxShadow:
    "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
  overflow: "hidden",
  mb: 2,
};

const headerStyle = {
  background: "linear-gradient(135deg, #303398 0%, #4F55D6 100%)",
  color: "#fff",
  px: 2.5,
  py: 1.75,
  display: "flex",
  alignItems: "center",
  gap: 1.25,
};

type SkuRow = {
  key: string;
  skuId: number;
  skuCode: string;
  price: string;
  stock: string;
  promo: string;
  promoStartDate: string;
  promoEndDate: string;
  isActive: boolean;
  imageUrl: string;
  combo: Record<number, string>;
};









const toDateInputStr = (val: number | string | null | undefined): string => {
  const ms = promoDateToMs(val);
  if (ms === null) return "";
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const fromDateInputStr = (val: string): string | null => {
  if (!val) return null;
  const d = new Date(`${val}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;
  // Drop the milliseconds so the payload matches the plain
  // "YYYY-MM-DDTHH:mm:ssZ" shape the backend sends/expects.
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
};

type EffectivePriceInfo = {
  value: number;
  isPromoActive: boolean;
  label: string;
};

// Decides what price actually applies "right now" for a SKU row, based on the promo
// price and its optional start/end date window:
//  - No promo entered              -> regular price.
//  - Promo entered, no dates at all -> promo price applies indefinitely (until user changes it).
//  - Promo entered with dates       -> promo price applies only while today is within
//                                       [start, end]. Before start (not started yet) or
//                                       after end (expired) -> falls back to regular price.
const getEffectivePrice = (row: Pick<SkuRow, "price" | "promo" | "promoStartDate" | "promoEndDate">): EffectivePriceInfo => {
  const regular = Number(row.price) || 0;
  const promoVal = row.promo !== "" && row.promo != null ? Number(row.promo) : null;

  if (promoVal == null || Number.isNaN(promoVal)) {
    return { value: regular, isPromoActive: false, label: "No promo" };
  }

  const hasStart = !!row.promoStartDate;
  const hasEnd = !!row.promoEndDate;

  if (!hasStart && !hasEnd) {
    return { value: promoVal, isPromoActive: true, label: "Promo active" };
  }

  const today = new Date().toISOString().slice(0, 10);

  if (hasStart && today < row.promoStartDate) {
    return { value: regular, isPromoActive: false, label: "Promo scheduled" };
  }
  if (hasEnd && today > row.promoEndDate) {
    return { value: regular, isPromoActive: false, label: "Promo expired" };
  }

  return { value: promoVal, isPromoActive: true, label: "Promo active" };
};

const mergeCategoryVariants = (
  a: Array<ICategoryVariant>,
  b: Array<ICategoryVariant>
): Array<ICategoryVariant> => {
  const byGroup: Record<number, ICategoryVariant> = {};
  [...a, ...b].forEach((g) => {
    if (!byGroup[g.categoryVariantId]) {
      byGroup[g.categoryVariantId] = { ...g, categoryVariantOptionList: [] };
    }
    const target = byGroup[g.categoryVariantId];
    g.categoryVariantOptionList.forEach((opt) => {
      const exists = target.categoryVariantOptionList.some(
        (o) =>
          o.categoryVariantOptionId === opt.categoryVariantOptionId ||
          (o.optionValue || "").trim().toLowerCase() === (opt.optionValue || "").trim().toLowerCase()
      );
      if (!exists) target.categoryVariantOptionList.push(opt);
    });
  });
  return Object.values(byGroup);
};

const toActiveBool = (value: number | string | boolean | null | undefined): boolean => {
  if (typeof value === "string") return value.trim() === "1" || value.trim().toLowerCase() === "true";
  return value === 1 || value === true;
};

const slugifyForSkuCode = (value: string): string =>
  (value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");

const cartesianProduct = (lists: string[][]): string[][] => {
  let result: string[][] = [[]];
  for (const list of lists) {
    const next: string[][] = [];
    for (const r of result) {
      for (const item of list) {
        next.push([...r, item]);
      }
    }
    result = next;
  }
  return result;
};

type Props = {
  productId: number;
  categoryId: number;
  mediaList?: Array<IMedia>;
  onSaved?: () => void;

  disabled?: boolean;
};

const ProductVariantsForm: React.FC<Props> = ({
  productId,
  categoryId,
  mediaList = [],
  onSaved,
  disabled = false,
}) => {

  const [standaloneVariants, setStandaloneVariants] = useState<Array<ICategoryVariant>>([]);
  const [comboVariants, setComboVariants] = useState<Array<ICategoryVariant>>([]);
  const categoryVariants = mergeCategoryVariants(standaloneVariants, comboVariants);

  const [catalogLoading, setCatalogLoading] = useState(true);
  const [comboLoading, setComboLoading] = useState(true);
  const loading = catalogLoading || comboLoading;

  const [saving, setSaving] = useState(false);
  const [selections, setSelections] = useState<Record<number, string[]>>({});
  const [rows, setRows] = useState<Array<SkuRow>>([]);

  const [newOptionValue, setNewOptionValue] = useState<Record<number, string>>({});
  const [addingOption, setAddingOption] = useState<Record<number, boolean>>({});
  const [newAttributeName, setNewAttributeName] = useState("");
  const [addingAttribute, setAddingAttribute] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const buildCategoryVariantsFromCombined = (
    section: Record<string, Array<any>> | undefined
  ): Array<ICategoryVariant> => {
    if (!section) return [];
    const byGroup: Record<number, ICategoryVariant> = {};
    Object.values(section).forEach((list: any) => {
      (Array.isArray(list) ? list : []).forEach((opt: any) => {
        const groupId = opt?.categoryVariantId;
        if (groupId == null) return;
        if (!byGroup[groupId]) {
          byGroup[groupId] = {
            categoryVariantId: groupId,
            categoryId: categoryId,
            variantName: opt.variantName || "",
            displayOrder: 1,
            isRequired: 1,
            isActive: 1,
            categoryVariantOptionList: [],
          };
        }
        if (
          opt.categoryVariantOptionId != null &&
          !byGroup[groupId].categoryVariantOptionList.some(
            (o) => o.categoryVariantOptionId === opt.categoryVariantOptionId
          )
        ) {
          byGroup[groupId].categoryVariantOptionList.push({
            categoryVariantOptionId: opt.categoryVariantOptionId,
            categoryVariantId: groupId,
            optionValue: opt.optionValue,
            displayOrder: byGroup[groupId].categoryVariantOptionList.length + 1,
            isActive: 1,
          });
        }
      });
    });
    return Object.values(byGroup);
  };

  const loadCatalog = useCallback(() => {
    if (!categoryId) {
      setStandaloneVariants([]);
      setCatalogLoading(false);
      return;
    }
    setCatalogLoading(true);
    CategoryService.getCategoryVariants(categoryId)
      .then((list) => setStandaloneVariants(list))
      .catch(() => setStandaloneVariants([]))
      .finally(() => setCatalogLoading(false));
  }, [categoryId]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const refreshComboCatalog = useCallback(async () => {
    if (!productId || !categoryId) return;
    try {
      const res: any = await ProductSkuService.getProductWithCategoryVariant(categoryId, productId);
      const body = unwrapData(res);
      const combined = buildCategoryVariantsFromCombined(body?.["category-variant"]);
      if (combined.length > 0) setComboVariants(combined);
    } catch {

    }

  }, [productId, categoryId]);

  useEffect(() => {
    if (!productId || !categoryId) {
      setComboLoading(false);
      return;
    }
    let cancelled = false;
    setComboLoading(true);
    ProductSkuService.getProductWithCategoryVariant(categoryId, productId)
      .then((res: any) => {
        if (cancelled) return;
        const body = unwrapData(res);

        const combined = buildCategoryVariantsFromCombined(body?.["category-variant"]);
        if (combined.length > 0) setComboVariants(combined);

        const rawCatalog = [...combined, ...standaloneVariants, ...comboVariants];

        const skuMap: Record<string, IProductSku> = body?.["product-sku"] || {};
        const skuList = Object.values(skuMap || {});
        if (skuList.length === 0 || rawCatalog.length === 0) return;

        const idLookup: Record<number, { groupId: number; value: string }> = {};
        rawCatalog.forEach((g) =>
          g.categoryVariantOptionList.forEach((o) => {
            idLookup[o.categoryVariantOptionId] = { groupId: g.categoryVariantId, value: o.optionValue };
          })
        );
        const groupIds = Array.from(new Set(rawCatalog.map((g) => g.categoryVariantId)));

        const nextSelections: Record<number, string[]> = {};
        const nextRows: Array<SkuRow> = [];

        skuList.forEach((sku) => {
          const combo: Record<number, string> = {};
          (sku.productSkuOptionsList || []).forEach((o) => {
            const found = idLookup[o.categoryVariantOptionId];
            if (found) {
              combo[found.groupId] = found.value;
              nextSelections[found.groupId] = Array.from(
                new Set([...(nextSelections[found.groupId] || []), found.value])
              );
            }
          });
          nextRows.push({
            key: groupIds.map((gid) => combo[gid] || "").join("|"),
            skuId: sku.skuId,
            skuCode: sku.skuCode || "",
            price: String(sku.price ?? ""),
            stock: String(sku.stock ?? ""),
            promo: sku.promo != null ? String(sku.promo) : "",
            promoStartDate: toDateInputStr(sku.promoStartDate),
            promoEndDate: toDateInputStr(sku.promoEndDate),
            isActive: toActiveBool(sku.isActive),
            imageUrl: sku.imageUrl || "",
            combo,
          });
        });

        setSelections(nextSelections);
        setRows(nextRows);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setComboLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productId, categoryId]);

  const regenerateRows = useCallback(
    (nextSelections: Record<number, string[]>) => {
      const activeGroups = categoryVariants.filter(
        (g) => (nextSelections[g.categoryVariantId] || []).length > 0
      );
      if (activeGroups.length === 0) {
        setRows([]);
        return;
      }
      const groups = activeGroups.map((g) => nextSelections[g.categoryVariantId] || []);
      const combos = cartesianProduct(groups);
      setRows((prevRows) => {
        const existingByKey: Record<string, SkuRow> = {};
        prevRows.forEach((r) => (existingByKey[r.key] = r));
        return combos.map((combo) => {
          const key = combo.join("|");
          if (existingByKey[key]) return existingByKey[key];
          const comboMap: Record<number, string> = {};
          activeGroups.forEach((g, i) => (comboMap[g.categoryVariantId] = combo[i]));
          // Auto-fill the SKU code from the product id + selected option
          // values (e.g. "142-SMALL-GRAY") so a row is already valid to




          const autoSkuCode = [productId, ...combo.map(slugifyForSkuCode)]
            .filter(Boolean)
            .join("-");
          return {
            key,
            skuId: 0,
            skuCode: autoSkuCode,
            price: "",
            stock: "",
            promo: "",
            promoStartDate: "",
            promoEndDate: "",
            isActive: true,
            imageUrl: "",
            combo: comboMap,
          };
        });
      });
    },
    [categoryVariants, productId]
  );

  const toggleOption = (groupId: number, value: string) => {
    setSelections((prev) => {
      const current = prev[groupId] || [];
      const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
      const merged = { ...prev, [groupId]: next };
      regenerateRows(merged);
      return merged;
    });
  };

  const handleAddOption = async (groupId: number) => {
    const value = (newOptionValue[groupId] || "").trim();
    if (!value) return;

    const alreadyExists = categoryVariants
      .find((g) => g.categoryVariantId === groupId)
      ?.categoryVariantOptionList.some((o) => (o.optionValue || "").trim().toLowerCase() === value.toLowerCase());

    if (alreadyExists) {
      toggleOption(groupId, value);
      setNewOptionValue((p) => ({ ...p, [groupId]: "" }));
      return;
    }

    setAddingOption((p) => ({ ...p, [groupId]: true }));
    try {
      const res: any = await CategoryService.addCategoryVariantOption(groupId, value);
      const created = unwrapData(res);
      const newOpt = {
        categoryVariantOptionId: created?.categoryVariantOptionId,
        categoryVariantId: groupId,
        optionValue: created?.optionValue || value,
        displayOrder: created?.displayOrder || 1,
        isActive: 1,
      };

      setComboVariants((prev) => {
        const exists = prev.some((g) => g.categoryVariantId === groupId);
        if (exists) {
          return prev.map((g) =>
            g.categoryVariantId === groupId
              ? { ...g, categoryVariantOptionList: [...g.categoryVariantOptionList, newOpt] }
              : g
          );
        }
        const base = categoryVariants.find((g) => g.categoryVariantId === groupId);
        if (!base) return prev;
        return [...prev, { ...base, categoryVariantOptionList: [...base.categoryVariantOptionList, newOpt] }];
      });

      setNewOptionValue((p) => ({ ...p, [groupId]: "" }));
      toggleOption(groupId, newOpt.optionValue);
    } catch (err: any) {
      setSnackbar({ open: true, message: getErrorMessage(err, "Could not add this option."), severity: "error" });
    } finally {
      setAddingOption((p) => ({ ...p, [groupId]: false }));
    }
  };

  const handleAddAttribute = async () => {
    const name = newAttributeName.trim();
    if (!name || !categoryId) return;

    const dupe = categoryVariants.find(
      (g) => (g.variantName || "").trim().toLowerCase() === name.toLowerCase()
    );
    if (dupe) {
      setSnackbar({ open: true, message: `"${name}" already exists below.`, severity: "error" });
      return;
    }

    setAddingAttribute(true);
    try {
      const res: any = await CategoryService.addCategoryVariant(categoryId, name);
      const created = unwrapData(res);
      const newGroup: ICategoryVariant = {
        categoryVariantId: created?.categoryVariantId,
        categoryId,
        variantName: created?.variantName || name,
        displayOrder: created?.displayOrder || 1,
        isRequired: created?.isRequired ?? 1,
        isActive: created?.isActive ?? 1,
        categoryVariantOptionList: [],
      };
      setStandaloneVariants((prev) => [...prev, newGroup]);
      setNewAttributeName("");
    } catch (err: any) {
      setSnackbar({ open: true, message: getErrorMessage(err, "Could not add this attribute."), severity: "error" });
    } finally {
      setAddingAttribute(false);
    }
  };

  const dedupeOptions = (list: ICategoryVariant["categoryVariantOptionList"]) => {
    const seen = new Set<string>();
    return list.filter((o) => {
      const key = (o.optionValue || "").trim().toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const updateRow = (key: string, patch: Partial<SkuRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const removeRowSelection = (row: SkuRow) => {
    const firstGroup = categoryVariants[0];
    if (!firstGroup) return;
    if (row.skuId > 0) {
      ProductSkuService.deleteProductSku(row.skuId).catch(() => {});
    }
    setRows((prev) => prev.filter((r) => r.key !== row.key));
  };

  const validateRows = (): string | null => {
    if (rows.length === 0) return "Generate at least one variant combination.";
    for (const r of rows) {
      if (!r.skuCode.trim()) return "SKU code is required for every row.";
      if (!r.price || Number(r.price) <= 0) return "Price must be greater than 0 for every row.";
      if (r.stock === "" || Number(r.stock) < 0) return "Stock is required for every row.";
      if (r.promoStartDate && r.promoEndDate && r.promoEndDate < r.promoStartDate) {
        return `Promo end date must be on or after the start date for SKU "${r.skuCode || r.key}".`;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const error = validateRows();
    if (error) {
      setSnackbar({ open: true, message: error, severity: "error" });
      return;
    }
    setSaving(true);
    try {

      const optionIdMap: Record<string, number> = {};
      categoryVariants.forEach((g) =>
        g.categoryVariantOptionList.forEach((o) => {
          optionIdMap[`${g.categoryVariantId}-${o.optionValue}`] = o.categoryVariantOptionId;
        })
      );

      const updatedRows: Array<SkuRow> = [];
      // Tracks whether ANY row asked for a promo date but the backend's
      // response came back without it, so we can flag it instead of
      // silently showing a false "saved successfully".
      let promoDateDroppedByServer = false;

      for (const row of rows) {
        const productSkuOptionsList = categoryVariants
          .filter((g) => row.combo[g.categoryVariantId])
          .map((g) => ({
            categoryVariantOptionId: optionIdMap[`${g.categoryVariantId}-${row.combo[g.categoryVariantId]}`],
          }))
          .filter((o) => !!o.categoryVariantOptionId);

        const sentPromoStart = fromDateInputStr(row.promoStartDate);
        const sentPromoEnd = fromDateInputStr(row.promoEndDate);

        const payload: Partial<IProductSku> = {
          price: Number(row.price) || 0,
          stock: Number(row.stock) || 0,
          promo: row.promo ? Number(row.promo) : null,
          promoStartDate: sentPromoStart,
          promoEndDate: sentPromoEnd,
          productId,
          skuCode: row.skuCode.trim(),
          isActive: row.isActive ? 1 : 0,
          imageUrl: row.imageUrl || null,
          productSkuOptionsList,
        };

        // Merge whatever the backend actually echoes back for the promo
        // dates into the row we keep in state, so the table always shows
        // the real saved value rather than assuming the payload we sent
        // was accepted as-is.
        const applyServerResponse = (savedSku: any, fallback: typeof row) => {
          const next: typeof row = {
            ...fallback,
            skuId: savedSku?.skuId ?? fallback.skuId,
            promoStartDate: toDateInputStr(savedSku?.promoStartDate),
            promoEndDate: toDateInputStr(savedSku?.promoEndDate),
          };
          if (
            (sentPromoStart && !savedSku?.promoStartDate) ||
            (sentPromoEnd && !savedSku?.promoEndDate)
          ) {
            promoDateDroppedByServer = true;
          }
          updatedRows.push(next);
        };

        if (row.skuId > 0) {

          const updatePayload: Partial<IProductSku> = { ...payload, skuId: row.skuId };
          try {
            const res: any = await ProductSkuService.updateProductSku(row.skuId, updatePayload);
            applyServerResponse(unwrapData(res), row);
          } catch {

            await ProductSkuService.deleteProductSku(row.skuId).catch(() => {});
            const res: any = await ProductSkuService.createProductSku(payload);
            applyServerResponse(unwrapData(res), row);
          }
        } else {
          const res: any = await ProductSkuService.createProductSku(payload);
          applyServerResponse(unwrapData(res), row);
        }
      }
      setRows(updatedRows);
      if (promoDateDroppedByServer) {
        setSnackbar({
          open: true,
          message:
            "Variants saved, but the server did not store one or more promo start/end dates — please check the backend's productSku date format.",
          severity: "error",
        });
      } else {
        setSnackbar({ open: true, message: "Variants saved successfully.", severity: "success" });
      }
      onSaved?.();
    } catch (err: any) {
      setSnackbar({ open: true, message: getErrorMessage(err, "Could not save the variants."), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!categoryId) {
    return (
      <Box sx={sectionStyle}>
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">Select a category in the General tab first.</Typography>
        </Box>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {disabled && (
        <Box sx={{ ...sectionStyle, p: 2, backgroundColor: "#fff8e6", border: "1px solid #f1b44c" }}>
          <Typography variant="body2" color="text.secondary">
            This product is published, so variants are read-only. Unpublish it from the banner above to make changes.
          </Typography>
        </Box>
      )}

      {!disabled && (
        <Box sx={sectionStyle}>
          <Box sx={headerStyle}>
            <AddRoundedIcon />
            <Typography variant="subtitle1" fontWeight={600}>
              Add New Attribute
            </Typography>
          </Box>
          <Box sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label="Attribute Name"
                placeholder="e.g. Color Family, Size"
                value={newAttributeName}
                onChange={(e) => setNewAttributeName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddAttribute();
                  }
                }}
                disabled={addingAttribute}
                sx={{ maxWidth: 260 }}
              />
              <Button
                variant="outlined"
                startIcon={addingAttribute ? <CircularProgress size={14} /> : <AddRoundedIcon />}
                disabled={addingAttribute || !newAttributeName.trim()}
                onClick={handleAddAttribute}
              >
                Add
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Attributes are saved against this product's category, so they'll be available for
              every product in this category going forward.
            </Typography>
          </Box>
        </Box>
      )}

      {categoryVariants.length === 0 ? (
        <Box sx={sectionStyle}>
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">
              No variant attributes yet for this category. Add one above (e.g. "Color Family") to
              get started.
            </Typography>
          </Box>
        </Box>
      ) : (
      <Box sx={sectionStyle}>
        <Box sx={headerStyle}>
          <TuneRoundedIcon />
          <Typography variant="subtitle1" fontWeight={600}>
            Select Variant Options
          </Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            {categoryVariants.map((group) => (
              <Box key={group.categoryVariantId}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75 }}>
                  {group.variantName}
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center">
                  {dedupeOptions(group.categoryVariantOptionList).map((opt) => {
                    const selected = (selections[group.categoryVariantId] || []).includes(opt.optionValue);
                    return (
                      <Chip
                        key={opt.categoryVariantOptionId}
                        label={opt.optionValue}
                        size="small"
                        color={selected ? "secondary" : "default"}
                        variant={selected ? "filled" : "outlined"}
                        disabled={disabled}
                        onClick={disabled ? undefined : () => toggleOption(group.categoryVariantId, opt.optionValue)}
                        sx={{ mb: 0.5, cursor: disabled ? "default" : "pointer" }}
                      />
                    );
                  })}
                </Stack>
                {!disabled && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <TextField
                      size="small"
                      placeholder={`Add a new ${group.variantName || "option"}...`}
                      value={newOptionValue[group.categoryVariantId] || ""}
                      onChange={(e) =>
                        setNewOptionValue((p) => ({ ...p, [group.categoryVariantId]: e.target.value }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddOption(group.categoryVariantId);
                        }
                      }}
                      disabled={!!addingOption[group.categoryVariantId]}
                      sx={{ maxWidth: 220 }}
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={
                        addingOption[group.categoryVariantId] ? (
                          <CircularProgress size={14} />
                        ) : (
                          <AddRoundedIcon />
                        )
                      }
                      disabled={
                        !!addingOption[group.categoryVariantId] ||
                        !(newOptionValue[group.categoryVariantId] || "").trim()
                      }
                      onClick={() => handleAddOption(group.categoryVariantId)}
                    >
                      Add
                    </Button>
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>
      )}

      {rows.length > 0 && (
        <Box sx={sectionStyle}>
          <Box sx={{ ...headerStyle, justifyContent: "space-between" }}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <TuneRoundedIcon />
              <Typography variant="subtitle1" fontWeight={600}>
                Combinations ({rows.length})
              </Typography>
            </Box>
            <Tooltip title="Regenerate combinations">
              <span>
                <IconButton
                  size="small"
                  sx={{ color: "#fff" }}
                  disabled={disabled}
                  onClick={() => regenerateRows(selections)}
                >
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: "block" }}>
            SKU codes are auto-filled from the product ID and selected options — feel free to edit
            them before saving.
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {categoryVariants.map((g) => (
                    <TableCell key={g.categoryVariantId}>{g.variantName}</TableCell>
                  ))}
                  <TableCell>SKU Code</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Stock</TableCell>
                  <TableCell>Promo</TableCell>
                  <TableCell>Promo Start</TableCell>
                  <TableCell>Promo End</TableCell>
                  <TableCell>Effective Price</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key}>
                    {categoryVariants.map((g) => (
                      <TableCell key={g.categoryVariantId}>
                        <Chip size="small" label={row.combo[g.categoryVariantId] || "-"} />
                      </TableCell>
                    ))}
                    <TableCell sx={{ minWidth: 170 }}>
                      <TextField
                        size="small"
                        disabled={disabled}
                        value={row.skuCode}
                        onChange={(e) => updateRow(row.key, { skuCode: e.target.value })}
                        sx={comboInputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 130 }}>
                      <TextField
                        size="small"
                        type="number"
                        disabled={disabled}
                        value={row.price}
                        onChange={(e) => updateRow(row.key, { price: e.target.value })}
                        sx={comboInputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <TextField
                        size="small"
                        type="number"
                        disabled={disabled}
                        value={row.stock}
                        onChange={(e) => updateRow(row.key, { stock: e.target.value })}
                        sx={comboInputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 120 }}>
                      <TextField
                        size="small"
                        type="number"
                        disabled={disabled}
                        value={row.promo}
                        onChange={(e) => updateRow(row.key, { promo: e.target.value })}
                        sx={comboInputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField
                        size="small"
                        type="date"
                        disabled={disabled}
                        InputLabelProps={{ shrink: true }}
                        value={row.promoStartDate}
                        onChange={(e) => updateRow(row.key, { promoStartDate: e.target.value })}
                        sx={comboInputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <TextField
                        size="small"
                        type="date"
                        disabled={disabled}
                        InputLabelProps={{ shrink: true }}
                        value={row.promoEndDate}
                        onChange={(e) => updateRow(row.key, { promoEndDate: e.target.value })}
                        sx={comboInputSx}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      {(() => {
                        const effective = getEffectivePrice(row);
                        return (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {effective.value.toFixed(2)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={effective.isPromoActive ? "success.main" : "text.secondary"}
                            >
                              {effective.label}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        disabled={disabled}
                        value={row.imageUrl}
                        onChange={(e) => updateRow(row.key, { imageUrl: e.target.value as string })}
                        sx={{ "& .MuiSelect-select": { display: "flex", alignItems: "center", gap: 1, py: "8px" } }}
                        renderValue={(value) => {
                          const selectedValue = value as string;
                          if (!selectedValue) {
                            return <Typography variant="body2" color="text.secondary">No image</Typography>;
                          }
                          const match = mediaList.find((m) => (m.col1 || m.dataUrl) === selectedValue);
                          return (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                component="img"
                                src={getMediaSrc({ col1: selectedValue })}
                                alt={match?.filename || "variant image"}
                                sx={{ width: 28, height: 28, borderRadius: "6px", objectFit: "cover", flexShrink: 0 }}
                              />
                              <Typography variant="body2" noWrap>
                                {match?.filename || "Selected image"}
                              </Typography>
                            </Box>
                          );
                        }}
                      >
                        <MenuItem value="">
                          <Typography variant="body2" color="text.secondary">No image</Typography>
                        </MenuItem>
                        {mediaList.length === 0 && (
                          <MenuItem value="" disabled>
                            <Typography variant="caption" color="text.secondary">
                              No images uploaded yet — add some in the Media tab first
                            </Typography>
                          </MenuItem>
                        )}
                        {mediaList.map((m) => (
                          <MenuItem key={m.mediaId} value={m.col1 || m.dataUrl}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                component="img"
                                src={getMediaSrc(m)}
                                alt={m.filename || "product image"}
                                sx={{ width: 28, height: 28, borderRadius: "6px", objectFit: "cover", flexShrink: 0 }}
                              />
                              <Typography variant="body2" noWrap>
                                {m.filename || m.col1 || m.dataUrl}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell align="center">
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            disabled={disabled}
                            checked={row.isActive}
                            onChange={(e) => updateRow(row.key, { isActive: e.target.checked })}
                          />
                        }
                        label=""
                        sx={{ m: 0 }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remove this combination">
                        <span>
                          <IconButton size="small" disabled={disabled} onClick={() => removeRowSelection(row)}>
                            <DeleteOutlineRoundedIcon fontSize="small" color="error" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      <Box display="flex" justifyContent="end" mt={1}>
        <Button
          variant="contained"
          color="secondary"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddRoundedIcon />}
          disabled={disabled || saving || rows.length === 0}
          onClick={handleSave}
          sx={{ borderRadius: "10px", px: 3 }}
        >
          {saving ? "Saving..." : "Save Variants"}
        </Button>
      </Box>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductVariantsForm;
